import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';
import { HttpClient } from '@angular/common/http';

import { Question } from '../../models/question.class';
import { Entry } from '../../models/entry.class';

/**
 * DataEndosigService
 * Provides data read from sources
 */
@Injectable()
export class DataEndosigService {
  /** List of questions */
  codebook: Array<Question>;
  /** Groups defined by populations of ENDOSIG survey */
  groups = [
    {
      'name': 'Gay (hombre cisgénero homosexual)',
      'slug': 'gay',
      'collapseBy': 'factor_expansion',
      'dummy': 'dum_gay',
    }, {
      'name': 'Lesbiana (mujer cisgénero homosexual)',
      'slug': 'lesbiana',
      'collapseBy': 'factor_expansion',
      'dummy': 'dum_lesbiana',
    }, {
      'name': 'Mujer bisexual (mujer cisgénero)',
      'slug': 'mujer_bisexual',
      'collapseBy': 'factor_expansion',
      'dummy': 'dum_muj_bi',
    }, {
      'name': 'Hombre bisexual (hombre cisgénero)',
      'slug': 'hombre_bisexual',
      'collapseBy': 'factor_expansion',
      'dummy': 'dum_hom_bi',
    }, {
      'name': 'Personas con otra orientación sexual no normativa',
      'slug': 'otra_orientacion_sexual',
      'collapseBy': 'factor_expansion',
      'dummy': 'dum_otro_sex',
    }, {
      'name': 'Hombre trans',
      'slug': 'hombre_trans',
      'collapseBy': 'factor_expansion',
      'dummy': 'hom_trans',
    }, {
      'name': 'Mujer trans',
      'slug': 'mujer_trans',
      'collapseBy': 'factor_expansion',
      'dummy': 'muj_trans',
    }, {
      'name': 'Personas con otra identidad de género no normativa',
      'slug': 'otra_orientacion_genero',
      'collapseBy': 'factor_expansion',
      'dummy': 'dum_otro_gen',
    }];

  /** Inits service, injects http client */
  constructor(private http: HttpClient) { }

  /** Returns the list of questions. Can return questions based on a selected topic */
  getCodebook(topic?: string): Observable<Question[]> {
    return this.http.get<Question[]>('/api/endosig/v1/question' + (topic ? '?topic=' + topic : ''));
  }

  /** Returns a question based on a specific question identifier */
  getQuestion(questionId: string): Observable<Question[]> {
    return this.http.get<Question[]>('/api/endosig/v1/question?questionId=' + questionId);
  }

  /**
   * Return data based on a selected question identifier and data should be parsed if
   * mode is specified
   */
  getEntries(questionId?, mode?): Observable<Entry[]> {
    let url = '/api/endosig/v1/entry?';
    if (questionId) {
      url += 'questionId=' + questionId + '&';
    }
    if (mode) {
      url += 'mode=' + mode;
    }
    return this.http.get<Entry[]>(url);
  }

  /** Returns columns that correspond to selected question */
  getColumnsFromQuestionId(questionId: string): Observable<Object[]> {
    return this.http.get<Object[]>('/api/endosig/v1/catalog/getColumnsFromQuestionId/' + questionId);
  }

  /** Returns categories (answers) that correspond to selected question */
  getRowsFromQuestionId(questionId: string): Observable<Object[]> {
    return this.http.get<Object[]>('/api/endosig/v1/catalog/getRowsFromQuestionId/' + questionId);
  }

  /**
   * Returns filter options that exist in database based on a selected question
   * and a specific column name
   */
  getFilterOptions(questionId: string, columnName: string): Observable<Object[]> {
    return this.http.get<Object[]>(`/api/endosig/v1/entry/getFilterOptions
      ?questionId=${questionId}&columnName=${columnName}`);
  }

  /**
   * Returns data ready to display in a chart based on selected question,
   * selected fitlers, option keys (units, axes, collapse by)
   */
  getDataToDraw(questionId: string, selectedFilters: object, options: object): Observable<Entry[]> {
    const url = '/api/endosig/v1/entry/getDataToDraw';
    const params = {
      questionId: questionId,
      keys: options,
      selectedFilters: selectedFilters,
      dropNAs: true
    };
    return this.http.post<Entry[]>(url, params);
  }

  /**
   * Returns data ready to display in a chart based on TWO questions.
   * Each question parameters should specify its own selected filters and option keys
   */
  crossData(firstQuestion: any, secondQuestion: any): Observable<Entry[]> {
    const url = '/api/endosig/v1/entry/crossData';
    const params = {
      firstQuestion: firstQuestion,
      secondQuestion: secondQuestion
    };
    return this.http.post<Entry[]>(url, params);
  }

  /** Returns groups defined for ENDOSIG population */
  getGroups() {
    return this.groups;
  }

  /** Return a group based on its name */
  findGroup(name: string) {
    return this.groups.find(g => g.name === name || g.slug === name);
  }

  /** Returns the sample size (N) of a selected question, based on selected filters
   * and option keys (axes, units, collapse by)
   */
  getSampleSize(questionId: string, selectedFilters: object, options: object): Observable<number> {
    const url = '/api/endosig/v1/entry/getSampleSize';
    const params = {
      questionId: questionId,
      keys: options,
      selectedFilters: selectedFilters,
      dropNAs: true
    };
    return this.http.post<number>(url, params);
  }
}



// WEBPACK FOOTER //
// ./src/app/services/data-endosig/data-endosig.service.ts